<template>
  <v-container fluid class="pa-0">
    <v-row align="center" justify="space-between" class="mb-5">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <div>
          <div class="page-title">Intern Matching System</div>
          <div class="page-subtitle">Find the perfect interns for your organization.</div>
        </div>
        <v-btn
          v-if="tabledata.length > 0 && !expandTab1"
          color="#00A63E"
          variant="flat"
          class="text-white"
          prepend-icon="mdi-plus"
          rounded="lg"
          size="large"
          @click="addInternRequest"
        >
          New Intern Request
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <!-- Empty state -->
      <v-col cols="12" v-if="tabledata.length == 0 && !expandTab1">
        <v-card flat rounded="xl" class="table-card pa-10 text-center">
          <div style="background-color: #dcfce7; width: 60px; height: 60px; margin: 0 auto;" class="rounded-xl d-flex align-center justify-center">
            <v-icon size="30" color="#00A63E">mdi-briefcase-search-outline</v-icon>
          </div>
          <div class="mt-4 font-weight-bold" style="font-size: 16px; color: #111827;">Create Your First Intern Request</div>
          <div class="text-grey mt-2" style="font-size: 13px; max-width: 400px; margin: 0 auto;">
            Specify your requirements and our system will find the best matching students for your organization.
          </div>
          <v-btn
            color="#00A63E"
            variant="flat"
            class="text-white mt-5"
            prepend-icon="mdi-plus"
            rounded="lg"
            size="large"
            @click="expandTab1 = !expandTab1"
          >
            Create Intern Request
          </v-btn>
        </v-card>
      </v-col>

      <!-- Requests table -->
      <v-col cols="12" v-if="tabledata.length > 0 && !expandTab1">
        <v-card flat rounded="xl" class="table-card">
          <div class="d-flex align-center justify-space-between pa-4">
            <div>
              <div class="card-title">Intern Requests</div>
              <v-chip size="x-small" color="#00A63E" variant="tonal" class="mt-1">{{ tabledata.length }} requests</v-chip>
            </div>
            <v-text-field
              v-model="search"
              placeholder="Search..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              style="max-width: 280px;"
            />
          </div>
          <v-divider />
          <v-data-table
            :headers="headers"
            :items="tabledata"
            :search="search"
            :loading="loading"
            hover
            class="rounded-b-xl"
          >
            <template v-slot:[`item.pg`]="{ item }">
              <v-chip
                v-for="(i, idx) in getProgramNames(item.programs)"
                :key="idx"
                size="small"
                variant="tonal"
                color="#00A63E"
                class="px-2 mx-1"
                >{{ i }}</v-chip
              >
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                color="#00A63E"
                variant="tonal"
                size="small"
                rounded="lg"
                @click="viewMatching(item)"
              >
                View Matches
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <!-- Create request / Matching view -->
      <v-col cols="12" v-if="expandTab1">
        <!-- Create form -->
        <v-card v-if="!showStudentMatching" flat rounded="xl" class="table-card">
          <div class="d-flex justify-space-between align-center pa-4">
            <div>
              <div class="card-title">Create Intern Request</div>
              <div style="font-size: 12px; color: #6b7280; margin-top: 2px;">Specify your requirements to find matching students</div>
            </div>
            <v-btn icon="mdi-close" variant="text" size="small" @click="expandTab1 = false" />
          </div>
          <v-divider />
          <v-form ref="InternRequestForm" class="pa-4">
            <div class="form-section-label mb-1">Number of Interns Needed</div>
            <v-text-field
              :rules="[formRules.required, formRules.onlyNumberNoNegative, formRules.noDecimal]"
              type="number"
              v-model="num_interns"
              variant="outlined"
              density="compact"
              placeholder="e.g. 5"
              style="max-width: 300px;"
            />
            <div class="form-section-label mt-3 mb-2">Preferred Programs</div>
            <div class="d-flex flex-wrap ga-3 pa-3 rounded-xl" style="border: 1px solid #e5e7eb;">
              <div v-for="i in programs" :key="i.id">
                <v-checkbox
                  @change="loadSkills()"
                  :rules="[formRules.required]"
                  :label="i.abbr"
                  :value="i.id"
                  v-model="programSelected"
                  hide-details
                  density="compact"
                />
              </div>
            </div>
            <div v-if="programSelected.length > 0" class="mt-4">
              <div class="form-section-label mb-2">Required Skills</div>
              <v-row>
                <v-col cols="3" v-for="i in skillsList" :key="i.id">
                  <div
                    class="pa-2 rounded-xl d-flex align-center ga-2 skill-item"
                    :class="{ 'skill-item--selected': selectedSkills.includes(i.title) }"
                    @click="toggleSkill(i.title)"
                    style="cursor: pointer;"
                  >
                    <v-icon size="14" :color="selectedSkills.includes(i.title) ? '#00A63E' : '#9ca3af'">
                      {{ selectedSkills.includes(i.title) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span style="font-size: 12px; font-weight: 500;">{{ i.title }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>
            <div class="mt-5 d-flex justify-end">
              <v-btn
                color="#00A63E"
                variant="flat"
                class="text-white"
                prepend-icon="mdi-check"
                rounded="lg"
                size="large"
                @click="save()"
              >
                Submit Request
              </v-btn>
            </div>
          </v-form>
        </v-card>

        <!-- Student matches -->
        <v-card v-else flat rounded="xl" class="table-card">
          <div class="d-flex justify-space-between align-center pa-4">
            <div>
              <div class="card-title">Student Matches</div>
              <div style="font-size: 12px; color: #6b7280; margin-top: 2px;">Students ranked by compatibility with your requirements</div>
            </div>
            <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" @click="expandTab1 = false">Back</v-btn>
          </div>
          <v-divider />
          <div class="pa-3 d-flex align-center ga-2" style="background-color: #fffbeb;">
            <v-icon size="16" color="warning">mdi-information-variant-circle-outline</v-icon>
            <span style="font-size: 12px; font-weight: 600; color: #92400e;">
              Suggested Candidates —
              <span style="font-weight: 400; color: #b45309;">These students partially match your requirements.</span>
            </span>
          </div>
          <v-divider />
          <div class="pa-4">
            <v-row>
              <v-col cols="6" v-for="i in 8" :key="i">
                <v-card flat rounded="xl" class="intern-card pa-4">
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center ga-3">
                      <div class="intern-avatar">
                        <v-icon color="#9ca3af" size="18">mdi-account</v-icon>
                      </div>
                      <div>
                        <div class="intern-name">Student Name</div>
                        <div class="intern-program">Program abbr</div>
                      </div>
                    </div>
                    <div class="text-center">
                      <div style="font-size: 18px; font-weight: 800; color: #ea580c;">30%</div>
                      <div style="font-size: 11px; color: #6b7280;">Match</div>
                    </div>
                  </div>
                  <v-divider class="my-3" />
                  <div class="d-flex ga-2">
                    <v-btn block variant="tonal" color="primary" size="small" rounded="lg">View Profile</v-btn>
                    <v-btn block variant="flat" color="#00A63E" size="small" rounded="lg" class="text-white">Request</v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="dialogConfirmDelete" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Confirm Deletion</v-card-title>
        <v-card-text class="text-body-1">
          Are you sure you want to delete this item?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogConfirmDelete = false"
            >Cancel</v-btn
          >
          <v-btn color="error" class="white--text" @click="confirmDelete"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </v-container>
</template>

<script>
export default {
  components: {},

  // props: {
  //   reloadTable: {
  //     type: Boolean,
  //     required: true,
  //   },
  // },
  data: () => ({
    search: "",
    qr_am_in: { type: "am_in", org_id: null },
    qr_am_out: { type: "am_out", org_id: null },
    qr_pm_in: { type: "pm_in", org_id: null },
    qr_pm_out: { type: "pm_out", org_id: null },
    size: 200,
    headers: [
      { title: "Number of Interns", value: "num_interns", align: "start" },
      { title: "Program", value: "pg", align: "start" },

      {
        title: "Actions",
        value: "actions",
        align: "center",
        sortable: false,
        width: 200,
      },
    ],
    data: [],
    skillsList: [],
    selectedSkills: [],
    programs_for: [],
    programSelected: [],
    perPageChoices: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "20", value: 20 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "250", value: 250 },
      { text: "500", value: 500 },
    ],
    expandTab1: false,
    expandTab2: false,
    programs: [],
    num_interns: null,
    showStudentMatching: false,
    itemData: null,
    viewData: null,
    loading: false,
    options: {},
    action: null,
    paginationData: {},
    formdata: [],
    tabledata: [],
    creds: null,
    dialogConfirmDelete: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Deleted!",
      message: "",
      top: 10,
    },
  }),
  mounted() {
    this.loadTable();
    this.initialize();
    // this.loadSkills();
  },
  beforeUnmount() {},
  methods: {
    pagination(data) {
      this.paginationData = data;
    },
    loadTable() {
      this.axiosCall("/organization/loadInternRequest", "GET").then((res) => {
        if (res) {
          console.log("sds  ", res.data);
          this.tabledata = res.data;
        }
      });
    },
    initialize() {
      // this.loading = true;
      // if (this.tab == 1) {
      this.axiosCall("/organization/getMyProgramsTied", "GET").then((res) => {
        if (res) {
          console.log("ORGS", res.data);
          this.programs = res.data;
          // this.loading = false;
        }
      });
      // } else if (this.tab == 2) {
      //   this.axiosCall("/student/get-batches", "GET").then((res) => {
      //     if (res) {
      //       console.log(res.data);
      //       this.batchData = res.data;
      //       this.loading = false;
      //     }
      //   });
      // } else if (this.tab == 3) {
      //   this.axiosCall("/student/get-batches-for-students", "GET").then(
      //     (res) => {
      //       if (res) {
      //         console.log(res.data);
      //         this.batchData = res.data;
      //         this.loading = false;
      //       }
      //     },
      //   );
      // }
    },

    getProgramNames(programs) {
      let arr = [];
      for (let i = 0; i < programs.length; i++) {
        for (let j = 0; j < this.programs.length; j++) {
          if (programs[i] == this.programs[j].id) {
            arr.push(this.programs[j].abbr);
          }
        }
      }
      return arr;
    },

    addInternRequest() {
      this.expandTab1 = !this.expandTab1;
      this.showStudentMatching = false;
    },

    loadSkills() {
      let progs = JSON.stringify(this.programSelected);
      console.log("PROGS", progs);
      if (this.programSelected.length > 0) {
        this.axiosCall(
          "/skill-assessement/getSkillCategoryByProgram/" + progs,
          "GET",
        ).then((res) => {
          if (res.data) {
            console.log(res.data);
            this.skillsList = res.data;
          }
        });
      }
    },

    async save() {
      let validate = await this.$refs.InternRequestForm.validate();
      if (validate.valid) {
        if (this.programSelected.length == 0) {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.header = "System Message";
          this.fadeAwayMessage.message = "Please select program.";
        } else {
          let data = {
            programSelected: JSON.stringify(this.programSelected),
            num_interns: this.num_interns,
            skills: JSON.stringify(this.selectedSkills),
          };

          this.axiosCall("/organization/add-intern-request", "POST", data).then(
            (res) => {
              if (res.data.status == 201) {
                this.fadeAwayMessage.show = true;
                this.fadeAwayMessage.type = "success";
                this.fadeAwayMessage.header = "System Message";
                this.fadeAwayMessage.message = res.data.msg;
                this.showStudentMatching = true;
              } else if (res.data.status == 400) {
                this.fadeAwayMessage.show = true;
                this.fadeAwayMessage.type = "error";
                this.fadeAwayMessage.header = "System Message";
                this.fadeAwayMessage.message = res.data.msg;
              }
            },
          );
        }
      }
    },

    viewMatching(item) {
      console.log(item);
      this.expandTab1 = true;
      this.showStudentMatching = true;

      this.axiosCall("/student/find-intern/byRequest/" + item.id, "GET").then(
        (res) => {
          console.log(res.data);
        },
      );
    },

    toggleSkill(title) {
      const idx = this.selectedSkills.indexOf(title);
      if (idx === -1) {
        this.selectedSkills.push(title);
      } else {
        this.selectedSkills.splice(idx, 1);
      }
    },

    viewItem(item) {
      this.itemData = item;
      this.action = "View";
    },

    manageItem(item) {
      this.creds = item;
    },
    confirmDelete() {
      this.axiosCall("/request-type/" + this.deleteData.id, "DELETE").then(
        () => {
          this.fadeAwayMessage.show = true;
          this.itemData = null;
          this.initialize();
        },
      );
    },
  },
};
</script>

<style scoped>
.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
}
.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 2px;
}
.table-card {
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.form-section-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}
.skill-item {
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  transition: all 0.15s;
}
.skill-item:hover {
  border-color: #86efac;
  background-color: #f0fdf4;
}
.skill-item--selected {
  border-color: #00A63E !important;
  background-color: #dcfce7 !important;
}
.intern-card {
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s;
}
.intern-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}
.intern-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.intern-name {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}
.intern-program {
  font-size: 12px;
  color: #6b7280;
}
</style>
